import { type ReactNode } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useAppSelector } from '@/hooks/useAppSelector';
import Loader from '@/ui-component/Loader';
import { hasAllPermissions, hasAnyPermission, hasPermission } from '@/utils/permissions';
import { NO_ACCESS_PATH, getUserHomePath } from '@/utils/role';

type PermissionGuardProps = {
    children: ReactNode;
    permission?: string;
    anyOf?: readonly string[];
    allOf?: readonly string[];
};

export const NoAccess = () => {
    const location = useLocation();
    const { user, permissions } = useAppSelector(({ auth }) => auth);
    const { profile } = useAppSelector(({ profile: profileState }) => profileState);
    const homePath = getUserHomePath(profile ?? user, permissions);
    const showHomeLink = homePath !== NO_ACCESS_PATH && homePath !== location.pathname;
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'background.paper',
                borderRadius: 2.5
            }}
        >
            <Typography variant="h3" component="h1">
                You don&apos;t have access
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                This page is not available for your account.
            </Typography>
            {showHomeLink ? (
                <Button component={RouterLink} to={homePath} variant="contained">
                    Go home
                </Button>
            ) : null}
        </Box>
    );
};

const PermissionGuard = ({ children, permission, anyOf, allOf }: PermissionGuardProps) => {
    const { isInitializing, permissions } = useAppSelector(({ auth }) => auth);

    if (isInitializing) {
        return <Loader />;
    }

    const allowed = allOf
        ? hasAllPermissions(permissions, allOf)
        : anyOf
          ? hasAnyPermission(permissions, anyOf)
          : hasPermission(permissions, permission ?? '');

    if (!allowed) {
        return <NoAccess />;
    }

    return children;
};

export default PermissionGuard;
